package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.Composable
import com.siriusxm.cmc.utils.Navigator
import org.jetbrains.compose.web.attributes.AttrsScope
import org.w3c.dom.HTMLAnchorElement
import org.jetbrains.compose.web.dom.A as HtmlA
import org.jetbrains.compose.web.dom.ContentBuilder

/**
 * A typed navigation destination that knows the URL it addresses.
 *
 * This exists so that no call site has to build a path string. A link is written as
 * `NavLink(ProducerDetail(id)) { ... }` rather than
 * `A(href = "#aggregator/producers/${encodeURIComponent(id)}")`, which means URL construction has
 * exactly one definition per destination family instead of one per call site.
 *
 * The interface is deliberately tiny — one function returning a [RouteLocation] — because that is all
 * a browser hash URL needs. Implementations are expected to be sealed families of keys (see
 * [ProducerKey]), so that the set of reachable destinations is closed and checked by the compiler.
 *
 * ### Keys are application-relative
 *
 * A key says where it is *within* its application; the mount point is not its business. That is why
 * there is no `appRoot` here — it used to be a field every key had to declare, which meant the
 * application prefix was restated once per family and [RouteLocation] had nowhere to put it. The
 * prefix now appears once, in [href], and the router owns it. (Shortcoming #1 of the UCA-1028 pilot;
 * see `plans/2026-08-28-UCA-1028-typed-producer-navigation.md`.)
 *
 * ### What stops a forgotten `encodeURIComponent`
 *
 * Being a [NavKey] is not by itself enough: an implementation whose [location] is a `when` returning
 * hand-built strings can still forget to encode a branch, and the compiler will not notice. What
 * closes that is how [ProducerKey] is built — each variant declares its **raw** segments and
 * [RouteLocation.toPath] encodes them in one place. Copy that arrangement, not just this interface.
 * If you add a family that interpolates an id into a segment itself, you have kept the type safety
 * and thrown away the property that motivated it.
 *
 * ### Scope
 *
 * This lives in the aggregator app, not in `cmc-ui` / `cmc-core`. Those are published to Artifactory
 * and consumed by the EPG app, so anything public added there becomes another team's maintenance
 * burden. If the pattern proves itself across more than one subtree here, promoting [NavKey] and
 * [NavLink] to `cmc-ui` is the natural next step — but that is a cross-team decision, not a local one.
 */
interface NavKey {
    /**
     * This destination as raw, application-relative path segments — e.g.
     * `["producers", "sxm/mddb-bus/mddb-event-bus", "errors"]` — plus any query state.
     *
     * Structured rather than a pre-built `String` so that percent-encoding has exactly one
     * implementation ([RouteLocation.toPath]) and so a destination can carry query parameters at all.
     * The flat-string version this replaced had nowhere to put them, which is why the producer filters
     * could not be shared. (Shortcoming #2 of the UCA-1028 pilot.)
     */
    fun location(): RouteLocation
}

/**
 * This destination as an absolute hash URL, suitable for an `<a href>`.
 *
 * The one place the aggregator's mount point is named, which is what lets keys stay
 * application-relative. Prefer [NavLink], which takes a [NavKey] directly; this is for the rare case
 * that needs the string itself (a `window.open`, a copy-to-clipboard action).
 */
fun NavKey.href(): String = "#$AGGREGATOR_ROOT/${location().toPath()}"

/**
 * Navigate to a typed destination.
 *
 * ### Why the receiver is [AggregatorService] and not [Navigator]
 *
 * It looks tempting to declare this on `Navigator` so any navigator can take a key, but that is
 * latently wrong: `interface Route : Navigator`, so inside a routing block the nearest receiver is
 * the `Route`, whose `navigate` is relative to its own `subPath` — a path assembled from *decoded*
 * components, which mangles a producer id containing a `/`. Worse, `HashNavigator`'s root route
 * defines `override fun navigate(path: String) { navigate(path) }`, an unconditional self-call, so a
 * relative navigation that climbs to the root overflows the stack. Both are in `cmc-core`, which is
 * published and not ours to fix. Constraining the receiver here means a call site cannot reach either.
 *
 * [AggregatorService.navigate] re-adds the application prefix, so an application-relative path is
 * what it wants.
 */
fun AggregatorService.navigate(key: NavKey) = navigate(key.location().toPath())

/**
 * Navigate to a typed destination, replacing its query state with [query].
 *
 * The single seam between the two halves of a URL: the key owns the path segments, the caller owns
 * the query, and neither type knows about the other. This is what keeps filter state out of the keys
 * — see `EventFilters` and `plans/2026-09-01-UCA-1028-producer-filter-url-state.md` §3.
 *
 * Any query the key itself carried is discarded, because a caller passing a query is stating the
 * complete query for the destination.
 */
fun AggregatorService.navigate(key: NavKey, query: Query) =
    navigate(RouteLocation(key.location().segments, query).toPath())

/**
 * An anchor to a typed navigation destination.
 *
 * Wraps Compose HTML's `A`, taking a destination instead of an href: `NavLink(ProducerDetail(id)) {
 * Text(name) }`. Deliberately a distinct name rather than an overload of `A` — a file that mixes typed
 * links with genuinely-string links (external URLs, for instance) reads better when the two are
 * visibly different, and a distinct name cannot be resolved to by accident.
 *
 * @param navKey the destination this link points at.
 * @param attrs additional attributes, as on the underlying `A`.
 * @param content the link's children.
 */
@Composable
fun NavLink(
    navKey: NavKey,
    attrs: (AttrsScope<HTMLAnchorElement>.() -> Unit)? = null,
    content: ContentBuilder<HTMLAnchorElement>? = null
) {
    HtmlA(href = navKey.href(), attrs = attrs, content = content)
}
